import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  CheckCircle2,
  XCircle,
  Clock,
  UtensilsCrossed,
  Loader2,
  Minus,
  Plus,
} from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/utils/cn';
import BookingHeader from '@features/booking/components/BookingHeader';
import { SummaryRow } from '@features/booking/components/SummaryRow';
import { BookingChrome } from '@features/booking/layouts';
import BookingFooter from '@features/booking/components/BookingFooter';
import { ApiError } from '@core/api/apiClient';
import { useTimeSlots } from '@features/booking/hooks/useTimeSlots';
import type { ServiceType } from '@features/booking/state/types';
import {
  getReservationDataEncrypted,
  postReservationAction,
} from '../api/reservations';
import type { ReservationAction } from '../api/reservations';
import { useRestaurant } from '@core/tenant/TenantProvider';
import {
  buildRescheduleDateTime,
  formatReservationDate,
  formatReservationTime,
  isGuaranteed,
  messageKeyForServerCode,
  reservationDateYmd,
  reservationTimeHm,
  resolveStatusView,
} from './reservationManage';

// UI-only stepper bounds. The backend (savePartySize availability check) is
// the real capacity authority; this max is just a guard to keep the stepper
// from wandering far past sane sizes.
const MIN_PARTY_SIZE = 1;
const MAX_PARTY_SIZE = 20;

const ReservationStatusPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('booking');
  const queryClient = useQueryClient();
  // The reservation payload does NOT carry a restaurant object — the current
  // restaurant comes from the tenant (subdomain) context, like every other
  // booking page. Reading it off the reservation (`data.restaurant.id`) is what
  // white-screened the deep link.
  const restaurant = useRestaurant();

  const [acting, setActing] = useState(false);
  const [actionResult, setActionResult] = useState<'confirmed' | 'cancelled' | 'updated' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['reservationStatus', token],
    queryFn: () => getReservationDataEncrypted(token!),
    enabled: !!token,
    retry: false,
    meta: { silentOn: [400, 401, 403, 404, 410, 422], source: 'reservation-status-link' },
  });

  // Local stepper state for party size, seeded from the reservation.
  const [partySize, setPartySize] = useState<number | null>(null);
  const currentPartySize = data?.partySize ?? MIN_PARTY_SIZE;
  const effectivePartySize = partySize ?? currentPartySize;
  // Never block the existing booking from being valid/increased: the cap must
  // always include the current size. The backend availability check is the
  // real capacity authority — this is only a UI guard.
  const maxPartySize = Math.max(MAX_PARTY_SIZE, currentPartySize);

  const view = data ? resolveStatusView(data) : null;
  const guaranteed = isGuaranteed(data?.guaranteeStatus);
  const canManage = view === 'manage' && !guaranteed;

  const dateYmd = data ? reservationDateYmd(data) : null;
  const currentTimeHm = data ? reservationTimeHm(data) : null;

  // Fetch the reservation day's slots only when the manage controls are usable.
  const slotsEnabled = !!(canManage && data && dateYmd && data.serviceType);
  const serviceTypes = useMemo<ServiceType[]>(
    () => (data?.serviceType ? [data.serviceType as ServiceType] : []),
    [data?.serviceType],
  );

  const { getServiceSlots, isLoading: slotsLoading } = useTimeSlots({
    restaurantId: restaurant.id,
    partySize: currentPartySize,
    date: dateYmd,
    serviceTypes,
    enabled: slotsEnabled,
  });

  const slots = data?.serviceType
    ? getServiceSlots(data.serviceType as ServiceType)
    : [];

  /**
   * Translate an ApiError thrown by the reservation-action endpoint into a
   * user-facing message. Prefers a per-status i18n string keyed on the backend
   * `status` (surfaced as `serverCode`), then the backend `message`, then a
   * generic fallback.
   */
  const messageForError = (err: unknown): string => {
    if (err instanceof ApiError) {
      const key = messageKeyForServerCode(err.serverCode);
      // A recognized server code maps to a specific message; otherwise prefer
      // the backend's human message before the generic fallback.
      if (key !== 'RESERVATION_STATUS_ERROR') return t(key);
      if (err.message && err.message !== 'An error occurred') return err.message;
    }
    return t('RESERVATION_STATUS_ERROR');
  };

  const refetchReservation = async () => {
    await queryClient.invalidateQueries({ queryKey: ['reservationStatus', token] });
  };

  const runAction = async (
    action: ReservationAction,
    extra?: { newResTime?: string; party_size?: number },
    onDone?: () => void,
  ) => {
    if (!token) return;
    setActing(true);
    setActionError(null);
    try {
      await postReservationAction(token, action, extra);
      onDone?.();
    } catch (err) {
      setActionError(messageForError(err));
    } finally {
      setActing(false);
    }
  };

  const handleConfirm = () =>
    runAction('confirm', undefined, () => setActionResult('confirmed'));

  const handleCancel = () =>
    runAction('cancel', undefined, () => setActionResult('cancelled'));

  const handleSavePartySize = () =>
    runAction('change_party_size', { party_size: effectivePartySize }, async () => {
      await refetchReservation();
      setActionResult('updated');
    });

  const handleSelectTime = (hour: string) => {
    if (hour === currentTimeHm) return;
    // Reschedule target is always the reservation's own date + the chosen
    // hour; null guards against an unparseable stored date.
    const rawResDate = data?.resDate?.date;
    const newResTime = rawResDate ? buildRescheduleDateTime(rawResDate, hour) : null;
    if (!newResTime) return;
    runAction('reschedule', { newResTime }, async () => {
      await refetchReservation();
      setActionResult('updated');
    });
  };

  const renderReservationInfo = () => {
    if (!data) return null;
    return (
      <div className="w-full max-w-sm px-4 space-y-4">
        <div className="bg-foreground/5 rounded-xl p-4 space-y-3">
          <SummaryRow label={t('RESERVATION_STATUS_RESTAURANT')} value={restaurant.name} />
          <SummaryRow
            label={t('RESERVATION_STATUS_DATE')}
            value={data.resDate?.date ? formatReservationDate(data.resDate.date) : (data.date ?? '')}
          />
          <SummaryRow
            label={t('RESERVATION_STATUS_TIME')}
            value={data.resTime?.date ? formatReservationTime(data.resTime.date) : (data.time ?? '')}
          />
          <SummaryRow label={t('RESERVATION_STATUS_GUESTS')} value={data.partySize} />
        </div>
      </div>
    );
  };

  const renderManage = () => {
    if (!data) return null;

    return (
      <>
        <div className="flex flex-col items-center pt-4">
          <h1 className="text-3xl font-display mb-2 text-center">
            {t('RESERVATION_STATUS_MANAGE_TITLE')}
          </h1>
          <p className="text-foreground/50 text-sm mb-8 text-center">
            {t('RESERVATION_STATUS_MANAGE_SUBTITLE')}
          </p>
        </div>

        {renderReservationInfo()}

        {actionError && (
          <p className="text-red-500 text-sm mt-4 text-center px-4 max-w-sm">{actionError}</p>
        )}

        {guaranteed ? (
          // Guaranteed bookings can't be self-modified — show only the contact
          // note + Cancel, so users never see controls that would 422.
          <div className="w-full max-w-sm px-4 mt-8 space-y-4">
            <p className="text-foreground/60 text-sm text-center">
              {t('RESERVATION_STATUS_GUARANTEED_CONTACT_NOTE')}
            </p>
            <Button
              variant="outline"
              className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleCancel}
              disabled={acting}
            >
              {acting ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <XCircle size={18} className="mr-2" />
              )}
              {t('WAITING_QUEUE_CANCEL_TABLE')}
            </Button>
          </div>
        ) : (
          <div className="w-full max-w-sm px-4 mt-8 space-y-8">
            {/* Change party size */}
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-foreground/70">
                {t('RESERVATION_STATUS_CHANGE_PARTY_TITLE')}
              </h2>
              <div className="flex items-center justify-center gap-6">
                <button
                  type="button"
                  className="w-11 h-11 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => setPartySize(Math.max(MIN_PARTY_SIZE, effectivePartySize - 1))}
                  disabled={acting || effectivePartySize <= MIN_PARTY_SIZE}
                  aria-label={t('RESERVATION_STATUS_CHANGE_PARTY_DECREASE')}
                >
                  <Minus size={18} />
                </button>
                <span
                  className="text-2xl font-semibold w-12 text-center text-brand-dark"
                  aria-live="polite"
                >
                  {effectivePartySize}
                </span>
                <button
                  type="button"
                  className="w-11 h-11 rounded-full bg-foreground/10 flex items-center justify-center text-brand-dark hover:bg-foreground/20 transition-colors disabled:opacity-30"
                  onClick={() => setPartySize(Math.min(maxPartySize, effectivePartySize + 1))}
                  disabled={acting || effectivePartySize >= maxPartySize}
                  aria-label={t('RESERVATION_STATUS_CHANGE_PARTY_INCREASE')}
                >
                  <Plus size={18} />
                </button>
              </div>
              <Button
                className="bg-brand text-brand-foreground font-medium py-5 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md disabled:opacity-50"
                onClick={handleSavePartySize}
                disabled={acting || effectivePartySize === currentPartySize}
              >
                {acting ? (
                  <Loader2 size={18} className="mr-2 animate-spin" />
                ) : null}
                {t('RESERVATION_STATUS_SAVE')}
              </Button>
            </section>

            {/* Change time (same day) */}
            <section className="space-y-3">
              <h2 className="text-sm font-medium text-foreground/70">
                {t('RESERVATION_STATUS_CHANGE_TIME_TITLE')}
              </h2>
              <p className="text-foreground/50 text-xs">
                {t('RESERVATION_STATUS_CHANGE_TIME_HINT')}
              </p>
              {slotsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 size={20} className="animate-spin text-brand" />
                </div>
              ) : slots.length === 0 ? (
                <p className="text-foreground/50 text-sm text-center py-4">
                  {t('RESERVATION_STATUS_CHANGE_TIME_NONE')}
                </p>
              ) : (
                <div
                  className="grid grid-cols-3 gap-2"
                  role="radiogroup"
                  aria-label={t('RESERVATION_STATUS_CHANGE_TIME_TITLE')}
                >
                  {slots.map((slot) => {
                    const isCurrent = slot.hour === currentTimeHm;
                    return (
                      <button
                        key={slot.hour}
                        type="button"
                        role="radio"
                        aria-checked={isCurrent}
                        disabled={acting || isCurrent}
                        onClick={() => handleSelectTime(slot.hour)}
                        className={cn(
                          'px-3 py-3 rounded-xl border shadow-sm transition-all text-base font-medium flex items-center justify-center gap-1.5',
                          'focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-1 disabled:opacity-70',
                          isCurrent
                            ? 'bg-brand text-brand-foreground border-brand cursor-default'
                            : 'bg-card text-foreground border-border hover:border-brand hover:text-brand',
                        )}
                      >
                        <Clock size={15} className={isCurrent ? 'text-brand-foreground' : 'text-brand'} />
                        {slot.hour}
                      </button>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Cancel */}
            <Button
              variant="outline"
              className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleCancel}
              disabled={acting}
            >
              {acting ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <XCircle size={18} className="mr-2" />
              )}
              {t('WAITING_QUEUE_CANCEL_TABLE')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderContent = () => {
    // No token
    if (!token) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_INVALID_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_INVALID_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Loading
    if (isLoading) {
      return (
        <div className="text-center text-foreground/40 text-sm py-12">
          {t('RESERVATION_STATUS_LOADING')}
        </div>
      );
    }

    // API error
    if (error || !data) {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_INVALID_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_INVALID_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Post-action: party size / time updated
    if (actionResult === 'updated') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_UPDATED_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_UPDATED_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => {
                // Clear the local stepper override so the manage view re-seeds
                // from the freshly refetched reservation data.
                setPartySize(null);
                setActionResult(null);
              }}
            >
              {t('RESERVATION_STATUS_MANAGE_DONE')}
            </Button>
          </div>
        </>
      );
    }

    // Post-action result
    if (actionResult === 'confirmed') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CONFIRMED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CONFIRMED_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (actionResult === 'cancelled') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
              <XCircle className="text-orange-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CANCELED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CANCELLED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    // Live status views
    if (view === 'manage') {
      return renderManage();
    }

    if (view === 'cancelled') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CANCELED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CANCELLED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'voided') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-6">
              <XCircle className="text-red-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('RESERVATION_STATUS_VOIDED_TITLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_VOIDED_SUBTITLE')}</p>
          </div>
          <div className="w-full max-w-sm px-4">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'confirmed') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-6">
              <CheckCircle2 className="text-green-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_CONFIRMED_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_CONFIRMED_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
              onClick={() => navigate('/')}
            >
              {t('RESERVATION_STATUS_BACK_TO_HOME')}
            </Button>
          </div>
        </>
      );
    }

    if (view === 'waiting') {
      return (
        <>
          <div className="flex flex-col items-center pt-4">
            <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mb-6">
              <Clock className="text-blue-600" size={32} />
            </div>
            <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_WAITING_TABLE')}</h1>
            <p className="text-foreground/50 text-sm mb-8 text-center">{t('RESERVATION_STATUS_WAITING_SUBTITLE')}</p>
          </div>
          {renderReservationInfo()}
          {actionError && (
            <p className="text-red-500 text-sm mt-4 text-center px-4">
              {actionError}
            </p>
          )}
          <div className="w-full max-w-sm px-4 mt-8">
            <Button
              variant="outline"
              className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
              onClick={handleCancel}
              disabled={acting}
            >
              {acting ? (
                <Loader2 size={18} className="mr-2 animate-spin" />
              ) : (
                <XCircle size={18} className="mr-2" />
              )}
              {t('WAITING_QUEUE_CANCEL_TABLE')}
            </Button>
          </div>
        </>
      );
    }

    // table_ready
    return (
      <>
        <div className="flex flex-col items-center pt-4">
          <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mb-6">
            <UtensilsCrossed className="text-orange-600" size={32} />
          </div>
          <h1 className="text-3xl font-display mb-2 text-center">{t('WAITING_QUEUE_TABLE_READY')}</h1>
          <p className="text-foreground/50 text-sm mb-8 text-center">{t('WAITING_QUEUE_NOTIFICATION_2')}</p>
        </div>
        {renderReservationInfo()}
        {actionError && (
          <p className="text-red-500 text-sm mt-4 text-center px-4">
            {actionError}
          </p>
        )}
        <div className="w-full max-w-sm px-4 mt-8 flex flex-col gap-3">
          <Button
            className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md"
            onClick={handleConfirm}
            disabled={acting}
          >
            {acting ? (
              <Loader2 size={18} className="mr-2 animate-spin" />
            ) : (
              <CheckCircle2 size={18} className="mr-2" />
            )}
            {t('WAITING_QUEUE_CONFIRM')}
          </Button>
          <Button
            variant="outline"
            className="font-medium py-6 px-8 rounded-xl w-full border-red-300 text-red-600 hover:bg-red-50"
            onClick={handleCancel}
            disabled={acting}
          >
            <XCircle size={18} className="mr-2" />
            {t('WAITING_QUEUE_CANCEL_TABLE')}
          </Button>
        </div>
      </>
    );
  };

  return (
    <BookingChrome flexCol>
        <BookingHeader />

        <main className="booking-content pt-8 pb-10 flex flex-col items-center flex-1">
          {renderContent()}
        </main>

        <footer className="border-t border-border">
          <div className="flex items-center px-6 py-4">
            <button
              aria-label="Go back"
              className="w-10 h-10 rounded-full border border-brand text-brand bg-card flex items-center justify-center hover:bg-brand/10 transition-colors cursor-pointer group shadow-sm"
              onClick={() => navigate('/')}
              type="button"
            >
              <ChevronLeft size={20} className="group-hover:scale-110 transition-transform" />
            </button>
          </div>
          <BookingFooter />
        </footer>
    </BookingChrome>
  );
};

export default ReservationStatusPage;
